import { Link } from 'react-router-dom'
import { Button } from '@cannect/new-components/atoms'
import { SUBSCRIPTION_DETAIL_PATH } from '@cannect/services/resources/recurrency/constants'
import {
  formatApiDate,
  getDocumentAlert,
  getPaymentAlert,
  getSubscriptionDisplayState,
  isPauseEndingSoon,
  pauseEndsAt
} from '@cannect/services/resources/recurrency/helpers'
import type { SubscriptionView } from '@cannect/services/resources/recurrency/types'
import { format } from 'date-fns'
import { StatusChip } from '../components/StatusChip'
import { ItemThumbs } from '../components/ItemThumbs'
import { subscriptionsCopy } from '../copy'
import { deepLinkSuffix, type DeepLinkTarget } from '../hooks/useSubscriptionDeepLink'

type SubscriptionCardProps = {
  subscription: SubscriptionView
  highlight?: DeepLinkTarget | null
}

type TopAction = { label: string; to: string; tone: 'alert' | 'primary' }

// Atalho no topo do card: documentação pendente (US7), cobrança recusada (US4),
// documento vencido (US6) ou o pedido de um link de e-mail (US5).
const getTopAction = (sub: SubscriptionView, highlight?: DeepLinkTarget | null): TopAction | null => {
  const detailPath = SUBSCRIPTION_DETAIL_PATH(sub.id)
  const open = sub.status === 'active' || sub.status === 'paused'
  if (!open) return null

  if (sub.documentsPending) {
    return {
      label: subscriptionsCopy.buttons.sendDocuments,
      to: sub.firstOrderId
        ? `/enviar-receita/${sub.firstOrderId}`
        : detailPath + deepLinkSuffix({ type: 'documents', doc: 'prescription' }),
      tone: 'alert'
    }
  }
  if (getPaymentAlert(sub) || (highlight?.type === 'card' && !sub.isDependent)) {
    return {
      label: subscriptionsCopy.buttons.changeCard,
      to: detailPath + deepLinkSuffix({ type: 'card' }),
      tone: getPaymentAlert(sub) ? 'alert' : 'primary'
    }
  }
  const documentsDown = getDocumentAlert(sub) && sub.pauseReason === 'document'
  if (documentsDown || (highlight?.type === 'documents' && !sub.isDependent)) {
    const doc = highlight?.type === 'documents' ? highlight.doc : 'prescription'
    return {
      label: subscriptionsCopy.buttons.sendDocuments,
      to: detailPath + deepLinkSuffix({ type: 'documents', doc }),
      tone: documentsDown ? 'alert' : 'primary'
    }
  }
  return null
}

export const SubscriptionCard = ({ subscription: sub, highlight }: SubscriptionCardProps) => {
  const state = getSubscriptionDisplayState(sub)
  const topAction = getTopAction(sub, highlight)
  const endsAt = isPauseEndingSoon(sub) ? pauseEndsAt(sub) : null
  const names = sub.items.map((item) => item.name).filter(Boolean)

  return (
    <article
      aria-label={sub.isDependent && sub.personName ? subscriptionsCopy.dependent(sub.personName) : 'Assinatura'}
      className="flex w-full flex-col gap-3 rounded-xl border border-solid border-neutral-100 bg-white p-4 shadow-sm md:p-6">
      {topAction && (
        <Button
          asChild
          size="md"
          full
          className={topAction.tone === 'alert' ? 'bg-red-700 text-white hover:bg-red-700/90' : undefined}>
          <Link to={topAction.to}>{topAction.label}</Link>
        </Button>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 flex-col">
          {sub.isDependent && sub.personName && (
            <span className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
              {subscriptionsCopy.dependent(sub.personName)}
            </span>
          )}
          <p className="text-sm font-semibold text-neutral-800">{state.sentence}</p>
        </div>
        <StatusChip state={state} />
      </div>

      {endsAt && (
        <p role="note" className="rounded-md bg-warning-100 px-3 py-2 text-sm text-neutral-800">
          {subscriptionsCopy.pauseEnding(format(endsAt, 'dd/MM'))}
        </p>
      )}

      <div className="flex items-center gap-3">
        <ItemThumbs items={sub.items} />
        <p className="min-w-0 truncate text-sm text-neutral-700">{names.join(', ')}</p>
      </div>

      <dl className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-neutral-600">
        <div className="flex gap-1">
          <dt className="sr-only">Intervalo</dt>
          <dd>{subscriptionsCopy.cycleEvery(sub.cycleDays)}</dd>
        </div>
        <div className="flex gap-1">
          <dt className="sr-only">Cartão</dt>
          <dd>
            {sub.card ? subscriptionsCopy.cardLabel(sub.card.brand, sub.card.lastDigits) : subscriptionsCopy.noCard}
          </dd>
        </div>
        {sub.status === 'paused' && sub.pausedUntil && sub.pauseReason !== 'patient' && (
          <div className="flex gap-1">
            <dt>Pausada até</dt>
            <dd>{formatApiDate(sub.pausedUntil, 'dd/MM')}</dd>
          </div>
        )}
      </dl>

      <div>
        <Button asChild variant="outline_light" size="md">
          <Link to={SUBSCRIPTION_DETAIL_PATH(sub.id)}>{subscriptionsCopy.buttons.details}</Link>
        </Button>
      </div>
    </article>
  )
}
